import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription, Box, Skeleton } from '@chakra-ui/react';

import { useAuth } from '@/lib/auth';
import { usePharma } from '@/providers/PharmaProvider';

import { BundleStatusReport } from './Modals/SiteReportModal';
import { ProtocolConfigProvider, useProtocolConfigContext } from './ProtocolConfigContext';
import { useClinicDevices } from './hooks/useClinicDevices';
import { useLayoutCounts } from './hooks/useLayoutCounts';
import { useBundleStatusMap } from './hooks/useBundleStatus';
import { useParticipantOverviewData } from './hooks/useParticipantOverviewData';
import { ProtocolConfig } from './types/protocolConfig';
import {
  CombinedApiData,
  DeviceMap,
  LayoutCountMap,
} from './interfaces/participantTrackerTypes';
import type { SptDataSource } from './types';

interface BundleStatusReportPanelProps {
  filterToCurrentSite?: boolean;
}

const normalizeSiteToken = (value: string | number | null | undefined): string => {
  // String() guard: numeric-only IDs (DFI subjects like 9999999001) have arrived
  // as JSON numbers from the overview API; never assume string here.
  return String(value ?? '').replace(/^SIENNA_/i, '').replace(/\D/g, '');
};

const participantMatchesSite = (participant: CombinedApiData, siteToken: string): boolean => {
  if (!siteToken) return true;
  const participantId = normalizeSiteToken(participant.Subject || participant.patient_user_name);
  const participantSite = normalizeSiteToken(participant['Site Number'] || participant.clinic_name);
  return participantSite === siteToken || participantId.startsWith(siteToken);
};

const BundleStatusReportPanelContent: React.FC<BundleStatusReportPanelProps> = ({
  filterToCurrentSite = false,
}) => {
  const auth = useAuth();
  const pharma = usePharma();

  // UAT data source toggle — available with the legacy 'UAT' clinic_flag (SIENNA)
  // or a study-scoped '{TRIAL}_UAT' flag (e.g. DFI18231_UAT). Study-scoped flags
  // exist because the literal UAT flag leaks clinic visibility across studies in
  // the any-flag-matches permission query (getPermissionedClinicIDs).
  const isUatEligible = (pharma.flags || []).some(f => f === 'UAT' || f.endsWith('_UAT'));
  const [sptSource, setSptSource] = useState<SptDataSource>('production');

  const { data: apiData, isLoading: isOverviewLoading, error: overviewError, refetch: refetchOverview } =
    useParticipantOverviewData(isUatEligible ? sptSource : 'production');
  const { data: clinicDevices, isLoading: isDevicesLoading, error: devicesError, refetch: refetchDevices } = useClinicDevices(isUatEligible ? sptSource : 'production');
  const { data: layoutCountsData, isLoading: isLayoutsLoading, error: layoutsError, refetch: refetchLayouts } = useLayoutCounts(isUatEligible ? sptSource : 'production');
  // Server-side protocol ruleset (single source of truth). Config is null
  // until the fetch resolves; rendering is gated below so bundle math never
  // races ahead of the protocol config.
  const { config: protocolConfig, isLoading: isProtocolLoading, error: protocolError, refetch: refetchProtocol } = useProtocolConfigContext();

  const handleRefetchData = useCallback(() => {
    refetchOverview();
    refetchDevices();
    refetchLayouts();
    refetchProtocol();
  }, [refetchOverview, refetchDevices, refetchLayouts, refetchProtocol]);

  const deviceMap = useMemo(() => {
    const map: DeviceMap = {};
    clinicDevices.forEach(device => {
      if (!map[device.patient_id]) map[device.patient_id] = [];
      map[device.patient_id].push(device);
    });
    return map;
  }, [clinicDevices]);

  const layoutMap = useMemo(() => {
    const map: LayoutCountMap = {};
    layoutCountsData.forEach(layout => {
      map[layout.patient_id] = layout;
    });
    return map;
  }, [layoutCountsData]);

  const siteUserName = useMemo(() => {
    if (!filterToCurrentSite) return '';
    return pharma.isCRC && pharma.isSiteLoggedIn
      ? pharma.selectedSubUser?.user_name || auth.user?.user_name
      : pharma.user?.user_name || auth.user?.user_name;
  }, [
    auth.user?.user_name,
    filterToCurrentSite,
    pharma.isCRC,
    pharma.isSiteLoggedIn,
    pharma.selectedSubUser?.user_name,
    pharma.user?.user_name,
  ]);

  const siteToken = useMemo(() => {
    return normalizeSiteToken(
      siteUserName
    );
  }, [
    siteUserName,
  ]);

  const participants = useMemo(() => {
    if (!filterToCurrentSite) return apiData;
    if (!siteToken) return [];
    return apiData.filter(participant => participantMatchesSite(participant, siteToken));
  }, [apiData, filterToCurrentSite, siteToken]);
  const siteScopeLabel = filterToCurrentSite && siteUserName ? siteUserName : undefined;

  const bundleStatusMap = useBundleStatusMap(participants);

  // Keep the skeleton up until the protocol config has actually arrived (the
  // extra clause covers the instant before the fetch effect flips isLoading).
  const isLoading = isOverviewLoading || isDevicesLoading || isLayoutsLoading
    || isProtocolLoading || (!protocolConfig && !protocolError);
  const error = protocolError || overviewError || devicesError || layoutsError;

  if (isLoading) {
    return <BundleStatusReportSkeleton hideSiteFilter={filterToCurrentSite} />;
  }

  if (error || !protocolConfig) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertDescription>{error || 'Protocol configuration unavailable.'}</AlertDescription>
      </Alert>
    );
  }

  return (
    <BundleStatusReport
      participants={participants}
      bundleStatusMap={bundleStatusMap}
      deviceMap={deviceMap}
      layoutMap={layoutMap}
      renderMode="page"
      enableExpandableRows
      isUatEligible={isUatEligible}
      sptSource={sptSource}
      onSptSourceChange={setSptSource}
      onRefetchData={handleRefetchData}
      hideSiteFilter={filterToCurrentSite}
      siteScopeLabel={siteScopeLabel}
    />
  );
};

/** Wraps the panel in the protocol-config provider so the whole tree (report,
 * skeleton, bundle status hooks) reads the server-driven config. */
export const BundleStatusReportPanel: React.FC<BundleStatusReportPanelProps> = props => (
  <ProtocolConfigProvider>
    <BundleStatusReportPanelContent {...props} />
  </ProtocolConfigProvider>
);

// ---------------------------------------------------------------------------
// Skeleton shown while the three API calls are in flight
// ---------------------------------------------------------------------------

/** Column definitions for the skeleton table — mirrors the real OverviewTable */
type SkeletonCol = { label: string; kind: 'text' | 'bundle' | 'date' };

// Placeholder bundle columns while the protocol config is still loading —
// purely cosmetic shimmer; the real columns come from the fetched config.
const SKELETON_PLACEHOLDER_BUNDLES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const buildSkeletonColumns = (config: ProtocolConfig | null): SkeletonCol[] => [
  { label: 'Participant', kind: 'text' },
  { label: 'Investigator Name', kind: 'text' },
  { label: 'Device', kind: 'text' },
  { label: 'Last Login', kind: 'date' },
  { label: 'Rand Date', kind: 'date' },
  { label: 'Tr. Created', kind: 'text' },
  { label: 'Loc. Created', kind: 'text' },
  { label: 'Home Created', kind: 'text' },
  ...(config?.bundles.milestones.map(ms => ms.bundleNumber) ?? SKELETON_PLACEHOLDER_BUNDLES).map(bundleNumber => ({
    label: `B${bundleNumber}`,
    kind: 'bundle' as const,
  })),
  { label: 'Next', kind: 'text' },
  { label: 'Next Start Window', kind: 'date' },
];


const BundleStatusReportSkeleton: React.FC<{ hideSiteFilter?: boolean }> = ({ hideSiteFilter = false }) => {
  const { config } = useProtocolConfigContext();
  const skeletonColumns = useMemo(() => buildSkeletonColumns(config), [config]);
  const shellRef = useRef<HTMLDivElement>(null);
  const [showScrollCue, setShowScrollCue] = useState(false);
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const check = () => setShowScrollCue(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
  <div className="sr-page relative font-sans text-gray-900 bg-white min-h-[640px] border border-gray-200 rounded-xl overflow-hidden">
    <div ref={shellRef} className="p-5 space-y-2.5">
      {/* Title + refresh — mirrors buildReportTopSection header */}
      <div className="mb-4 space-y-2.5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-base font-bold text-gray-900">
              {config ? `${config.metadata.studyName} — ` : ''}VVP Bundle Status Report
            </div>
            <div className="mt-0.5 text-[10px] text-gray-500">
              {config
                ? `Protocol ${config.metadata.protocolId}`
                : <Skeleton as="span" display="inline-block" h="10px" w="120px" borderRadius="sm" />}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-400 shrink-0">
            <Skeleton h="10px" w="100px" borderRadius="sm" />
            <div className="h-7 w-[60px] rounded-md border border-gray-300 bg-white" />
          </div>
        </div>

        {/* Tab bar — real labels, shimmer counts */}
        <div className="flex flex-wrap items-end gap-5 border-b border-gray-200 pt-0.5">
          {['Randomized', 'Screening', 'Screen Failed / Not Participating'].map((label, i) => (
            <span key={label} className={`relative -mb-px pb-1.5 text-[11px] font-semibold ${i === 0 ? 'text-blue-900' : 'text-gray-400'}`}>
              {label}{' '}
              <Skeleton as="span" display="inline-block" h="9px" w="18px" verticalAlign="middle" borderRadius="sm" />
              {i === 0 && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-900" />}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll cue placeholder — reserves same space as sr-scroll-cue (18px + 3px bottom padding) */}
      {showScrollCue && <div style={{ height: '8px' }} />}

      {/* Filter bar + KPI strip inline — mirrors filterBar layout */}
      <div className="flex gap-2.5 items-stretch mb-2.5" style={{ minWidth: '1280px' }}>
        {/* Left: filter controls */}
        <div className="flex-1 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
          {config?.cohorts && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Cohort</span>
                <div className="flex h-7 overflow-hidden rounded-md border border-gray-300 bg-white">
                  {['All', 'A', 'B'].map((cohort, index) => (
                    <button
                      key={cohort}
                      type="button"
                      className={`px-2.5 text-[11px] font-semibold ${index > 0 ? 'border-l border-gray-300' : ''} ${
                        cohort === 'All' ? 'bg-blue-900 text-white' : 'bg-white text-gray-600'
                      }`}
                      disabled
                    >
                      {cohort}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-5 w-px bg-gray-300" />
            </>
          )}
          {!hideSiteFilter && (
            <input
              className="h-7 w-[100px] rounded-md border border-gray-300 bg-white px-2 text-[11px]"
              placeholder="Site"
              disabled
            />
          )}
          <input
            className="h-7 w-[130px] rounded-md border border-gray-300 bg-white px-2 text-[11px]"
            placeholder="Participant ID"
            disabled
          />
          <div className="ml-auto flex items-center gap-2 text-[10px] text-gray-400">
            <span>Loading&hellip;</span>
          </div>
        </div>
        {/* Right: KPI cards shimmer */}
        <div className="grid shrink-0 overflow-hidden rounded-lg border border-gray-200 grid-cols-2">
          {[
            { title: 'Overall test compliance', bg: '#fff' },
            { title: 'B1 (Baseline) test compliance', bg: '#f1f5f9' },
          ].map(({ title, bg }, i) => (
            <div
              key={title}
              className={`h-[88px] px-3 py-2 ${i === 0 ? 'border-r border-gray-200' : ''}`}
              style={{ background: bg }}
            >
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-700">{title}</div>
              <div className="flex flex-col gap-1">
                {(config?.cohorts ? ['Cohort A', 'Cohort B'] : ['Overall']).map(label => (
                  <div key={label} className="flex h-6 items-center gap-2 text-[10px]">
                    <span className="w-14 shrink-0 font-semibold leading-none text-gray-700">{label}</span>
                    <Skeleton h="16px" w="28px" borderRadius="sm" />
                    <Skeleton h="10px" w="40px" borderRadius="sm" />
                    <div className="h-1.5 w-[180px] shrink-0 overflow-hidden rounded-full bg-gray-200" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table area — real header, simple loading body */}
      <div className="overflow-hidden">
        <table className="sr-overview-table w-full border-collapse text-[10px]" style={{ tableLayout: 'auto', minWidth: '1280px' }}>
          <thead>
            <tr>
              {skeletonColumns.map(col => {
                const isB1 = col.label === 'B1';
                const isBundle = col.kind === 'bundle';
                const isParticipant = col.label === 'Participant';
                const isRandDate = col.label === 'Rand Date';
                const isNextWindow = col.label === 'Next Start Window';
                return (
                  <th
                    key={col.label}
                    className="px-1.5 py-1 text-[9px] font-bold text-white bg-gray-600 border-b-2 border-gray-800 whitespace-nowrap"
                    style={{
                      textAlign: isBundle ? 'center' : 'left',
                      ...(isB1 ? { width: '54px', minWidth: '54px', background: '#f1f5f9', borderColor: '#cbd5e1', color: '#374151' } : {}),
                      ...(isBundle && !isB1 ? { width: '44px', minWidth: '44px' } : {}),
                      ...(isNextWindow ? { minWidth: '120px' } : {}),
                    }}
                  >
                    {isParticipant ? (
                      <span className="inline-flex items-center">
                        <span className="inline-block w-3.5 shrink-0" />
                        {col.label}
                      </span>
                    ) : isB1 ? (
                      <span className="inline-flex flex-col items-center leading-none">
                        <span>B1</span>
                        <span className="mt-0.5 font-sans text-[7px] font-medium text-gray-500">
                          {config?.bundles.milestones[0]?.windowMode === 'pre_anchor' ? '(pre-D1)' : '(D1 only)'}
                        </span>
                      </span>
                    ) : isRandDate ? (
                      <span className="inline-block" style={{ paddingLeft: '20px' }}>{col.label}</span>
                    ) : col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
        </table>
        <div className="flex items-center justify-center py-20 text-xs text-gray-400">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading participant data&hellip;
        </div>
      </div>
    </div>
  </div>
  );
};

export default BundleStatusReportPanel;
